import type { DoorbellCallInfo } from "@/lib/api/doorbell-calls";
import type { CallState } from "./protocol";
import type { CallSnapshot } from "./session";

/** How the call page joins: watch a ringing call (to answer it), rejoin the
 *  call this tab answered (after a reload), or not at all (show the outcome). */
export type CallMode = "watch" | "rejoin" | null;

export function initialCallMode(info: Pick<DoorbellCallInfo, "state"> | undefined, answeredHere: boolean): CallMode {
  if (!info) return null;
  if (info.state === "ringing") return "watch";
  if (info.state === "connected" && answeredHere) return "rejoin";
  return null;
}

export function outcomeText(state: CallState | null, answeredByName: string | null): string {
  switch (state) {
    case "connected":
      return answeredByName ? `Answered by ${answeredByName}` : "Someone else answered";
    case "missed":
      return "Missed call";
    case "declined":
      return "Declined";
    case "cancelled":
      return "The visitor cancelled the call";
    default:
      return "Call ended";
  }
}

/** The page's one status line: the session's phase once this page acts, the server's state before. */
export function callStatusText(
  info: Pick<DoorbellCallInfo, "state" | "asking_for" | "location" | "answered_by_name" | "declined_by_you"> | undefined,
  snap: CallSnapshot,
  isTest: boolean,
): string {
  switch (snap.phase) {
    case "acquiring":
      return "Starting your camera or microphone…";
    case "accepting":
      return "Answering…";
    case "connected":
      return isTest ? "Test call connected" : "Connected";
    case "reconnecting":
      return "Reconnecting…";
    case "ended":
      return "Call ended";
    case "taken":
      return outcomeText(snap.serverState, snap.answeredByName);
    case "replaced":
      return "This call continued in another window";
    case "failed":
      return snap.error ?? "The call failed.";
    default:
      break;
  }
  if (!info) return "Loading…";
  // Final for this member even while the call still rings for others.
  if (info.declined_by_you) return "You declined this call.";
  if (info.state === "ringing") return `Visitor at ${info.location || "reception"} asking for ${info.asking_for}`;
  return outcomeText(info.state, info.answered_by_name);
}
